import OpenPositions from "@/components/OpenPositions";
import SEO from "@/components/SEO";
import { getClient } from "@/lib/sanity/server";
import { GetStaticProps, NextPage } from "next";
import { groq } from "next-sanity";
import {
  filterDataToSingleItem,
  usePreviewSubscription,
} from "@/lib/sanity/client";
import { seoQuerySchema } from "@/lib/sanity/queries";
import { z } from "zod";

const jobsPageDataSchema = z.object({
  _id: z.string(),
  seo: seoQuerySchema,
  heading: z.string().nullable(),
  jobApplication: z
    .object({
      heading: z.string().nullable(),
      excerpt: z.string().nullable(),
      linkTitle: z.string().nullable(),
    })
    .nullable(),
});

type JobsPageData = z.infer<typeof jobsPageDataSchema>;

const jobsPageQuery = groq`
*[_type == "jobsPage"][0] {
    _id,
    seo {
        "metaTitle": coalesce(metaTitle[$locale], metaTitle[$defaultLocale]),
        "metaDesc": coalesce(metaDesc[$locale], metaDesc[$defaultLocale]),
        "shareTitle": coalesce(shareTitle[$locale], shareTitle[$defaultLocale]),
        "shareDesc": coalesce(shareDesc[$locale], shareDesc[$defaultLocale]),
        shareGraphic {
          asset->{
            ...,
            "alt": coalesce(alt[$locale], alt[$defaultLocale])
          }
        }
    },
    "heading": coalesce(heading[$locale], heading[$defaultLocale]),
    jobApplication {
        "heading": coalesce(heading[$locale], heading[$defaultLocale]),
        "excerpt": coalesce(excerpt[$locale], excerpt[$defaultLocale]),
        "linkTitle": coalesce(linkTitle[$locale], linkTitle[$defaultLocale])
    }
}
`;

type Props = {
  preview: boolean;
  data: {
    page: JobsPageData;
    query: string;
    queryParams: {
      slug: string;
      locale: string;
      defaultLocale: string;
    };
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({
  params,
  preview = false,
  locale = "it",
  defaultLocale = "it",
}) => {
  const queryParams = {
    slug: "people/open-positions",
    locale,
    defaultLocale,
  };

  const queryResponse = await getClient(preview).fetch(
    jobsPageQuery,
    queryParams
  );
  const data = jobsPageDataSchema.parse(queryResponse);
  if (!data) return { notFound: true };
  // Helper function to reduce all returned documents down to just one
  const page = filterDataToSingleItem<JobsPageData>(data, preview);

  return {
    props: {
      preview,
      data: {
        page,
        query: jobsPageQuery,
        queryParams,
      },
    },
    revalidate: 60,
  };
};

const Page: NextPage<Props> = ({ data, preview }) => {
  const { data: previewData } = usePreviewSubscription(data?.query, {
    params: data?.queryParams,
    // The hook will return this on first render
    // This is why it's important to fetch *draft* content server-side!
    initialData: data?.page,
    // The passed-down preview context determines whether this function does anything
    enabled: preview,
  });

  // Client-side uses the same query, so we may need to filter it down again
  const page = filterDataToSingleItem(previewData, preview);

  return (
    <>
      {page.seo && <SEO {...page.seo} slug={data?.queryParams?.slug} />}
      {page && (
        <OpenPositions
          id={page?._id}
          heading={page?.heading}
          jobApplication={{ ...page?.jobApplication }}
        />
      )}
    </>
  );
};

export default Page;
